<template>
  <div class="manual">
    <aside class="toc">
      <div class="toc-title">目录</div>
      <a
        v-for="s in sections"
        :key="s.id"
        :class="{ active: activeId === s.id }"
        href="javascript:;"
        @click="scrollTo(s.id)"
      >{{ s.title }}</a>
    </aside>
    <div class="manual-body" ref="body">
      <div class="intro-box">
        <h2>用户操作手册</h2>
        <p>按制造执行的上手顺序编写，覆盖 PC 管理端和车间 App。建议首次使用时按章节走一遍主数据、工单、报工和库存。</p>
        <div class="quick">
          <div class="quick-title">快速上手</div>
          <ol>
            <li>PC：公司、用户角色 → 产品、工序、工艺、车间产线、仓库、员工</li>
            <li>PC：生产工单受理并开工 → 工序任务开工</li>
            <li>App：绑定员工 → 扫码或打开已开工任务 → 保存报工再确认</li>
            <li>PC：核对自动生成的领料与入库；需要时做发货、采购到货、盘点</li>
            <li>设备保养走维保计划生成工单；现场故障用 App 报修</li>
          </ol>
        </div>
      </div>

      <section v-for="s in sections" :id="s.id" :key="s.id" class="chapter">
        <h3>{{ s.title }}</h3>
        <div v-for="(block, bi) in s.blocks" :key="bi" class="block">
          <h4 v-if="block.heading">{{ block.heading }}</h4>
          <p v-if="block.path" class="path">菜单位置：{{ block.path }}</p>
          <p v-if="block.text" class="text">{{ block.text }}</p>
          <ol v-if="block.steps && block.steps.length">
            <li v-for="(t, i) in block.steps" :key="'s' + i">{{ t }}</li>
          </ol>
          <ul v-if="block.bullets && block.bullets.length">
            <li v-for="(t, i) in block.bullets" :key="'b' + i">{{ t }}</li>
          </ul>
          <p v-if="block.tip" class="tip">提示：{{ block.tip }}</p>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
const sections = [
  {
    id: 'ch1',
    title: '一、认识两端与账号',
    blocks: [
      {
        heading: '1.1 产品定位',
        text: '华锐智造云是制造执行系统：PC 做计划、工艺、库存和维保，车间 App 做已开工工序的报工、停机、报修和仓库草稿执行。'
      },
      {
        heading: '1.2 如何进入',
        bullets: [
          'PC：浏览器打开 https://mes.harence.com ，搜索并选择企业后登录',
          '车间 App：在 PC 登录页扫描「扫码下载 App」，按提示用浏览器下载安卓版',
          'App 与 PC 须能访问同一套服务；现场手机和电脑在同一网络时，才能连到本地服务'
        ]
      },
      {
        heading: '1.3 账号',
        bullets: [
          '系统用户：在「系统管理 → 用户管理」开通，能进 PC；菜单由角色决定',
          '车间员工：在「人员管理 → 员工管理」建档。App 登录后到「我的」绑定这名员工，才能报工、打卡',
          '免费版含 1 个系统账号，标准版 10 个，专业版 20 个。达到上限后不能再新增用户'
        ],
        tip: '忘记密码可在登录页用短信重置。员工档案不是系统登录账号。'
      }
    ]
  },
  {
    id: 'ch2',
    title: '二、组织与主数据（PC）',
    blocks: [
      {
        heading: '2.1 公司与权限',
        path: '系统管理 → 公司信息 / 部门管理 / 角色管理 / 用户管理',
        text: '建议顺序：公司信息 → 部门 → 角色勾选菜单 → 用户分配角色。',
        tip: '套餐订阅里可看当前套餐。标准版、专业版支持微信或支付宝在线支付，也可对公转账后提交认款。'
      },
      {
        heading: '2.2 产品与工艺',
        path: '基础信息 → 产品管理；工艺管理 → 工序管理 / 工艺管理',
        steps: [
          '先维护产品（物料或成品）的编码、名称、单位',
          '工序是可复用的标准工序',
          '工艺挂到产品上，写清工艺路线、投料和产出。工单按工艺展开工序任务和用料'
        ]
      },
      {
        heading: '2.3 工厂、产线、仓库、人员',
        path: '公司架构 → 车间 / 产线；仓库管理 → 仓库；人员管理 → 员工管理',
        bullets: [
          '车间、产线用于工单和设备归属',
          '仓库区分原料仓、成品仓等，报工扣料和入库都要落到具体仓库',
          '员工供 App 绑定。班次、工作日历在「产线日历」'
        ]
      }
    ]
  },
  {
    id: 'ch3',
    title: '三、生产工单与工序',
    blocks: [
      {
        heading: '3.1 生产工单',
        path: '生产计划 → 生产工单',
        steps: [
          '可从「销售管理 → 主订单」带出需求，也可以直接新建工单',
          '草稿受理后才能往下做。开工后进入生产中',
          '「进度大屏」看当前工序，临近交期和逾期会标出'
        ]
      },
      {
        heading: '3.2 工序任务',
        path: '生产计划 → 工序任务',
        bullets: [
          '工单按工艺生成工序任务。状态：待执行、已开工、已完成、已取消',
          '待执行要点「开工」后，车间 App 才能看到并报工',
          '排程可用简易排程、甘特排程、作业计划和班次任务'
        ],
        tip: 'App 任务列表只显示已开工。扫工单流转卡也只列出已开工工序；直接扫某一道工序任务码，仍会打开该任务的报工页。'
      },
      {
        heading: '3.3 物料需求',
        path: '供应管理 → 物料需求 / 齐套看板',
        text: '按工单展开用料，可看齐套情况。齐套看板可以按多仓生成领料草稿。'
      }
    ]
  },
  {
    id: 'ch4',
    title: '四、报工、异常与计件',
    blocks: [
      {
        heading: '4.1 PC 报工',
        path: '生产跟踪 → 生产报工',
        steps: [
          '先保存草稿，只记产出批次等信息',
          '确认时选择仓库、投入批次；若该用料行指定了替代料，才能改用替代料',
          '确认数量由服务器按工艺用量计算，页面上的数量不能手改成另一套账'
        ],
        tip: '本道工序已确认的合格数，加上本次合格数，不能超过本道工序计划数。报废不占用计划数。'
      },
      {
        heading: '4.2 确认之后',
        bullets: [
          '确认后按合格数量自动领料，并做生产入库。这两类单据是已审核的，不会出现在 App 的草稿领料列表',
          '扣料仓库默认取组件库位或组件仓库；确认时如果指定了仓库，按指定仓库扣',
          '「报工登记簿」可回看已登记记录'
        ]
      },
      {
        heading: '4.3 异常、停机、打卡',
        path: '生产跟踪 → 报工异常 / 停机登记 / 员工打卡 / 计件计时结算',
        bullets: [
          '报工异常登记质量问题。返工会把待执行或已完成的工序重新打开为已开工，不会把异常数量加回原工序计划',
          '停机登记记录产线、设备、原因和起止时间',
          '打卡记的是工时。App「当前计时」只显示绑定员工有没有未结束的计时，开始和结束在报工页操作。报工不要求先打卡，同一人同时只能有一段未结束计时',
          'App「我的工资」按计件，不按打卡工时'
        ]
      }
    ]
  },
  {
    id: 'ch5',
    title: '五、库存、领料与发货',
    blocks: [
      {
        heading: '5.1 库存单据',
        path: '仓库管理 → 库存查询 / 库存单据',
        bullets: [
          '领料、退料、移库、生产入库、发货出库、采购入库都在库存单据',
          '未审核是草稿，审核后才改现存量',
          '手工领料要填单头的出库仓。只在明细行填仓库时，App 按当前仓库过滤会看不到这张草稿'
        ]
      },
      {
        heading: '5.2 App 仓库',
        text: '先在仓库首页选当前仓库。领料、发货出库等列表只显示这个仓库的草稿，并只取第一页。',
        bullets: [
          '领料：改实发数量后「保存并审核」。实发少于申请不会自动再开一张单',
          '领料列表里的「扫码领料」按流转卡打开或生成草稿',
          '查库存按所选仓库查询，和 PC 不选仓库时的全库条数可能不同',
          '另有盘点、重包装、备料'
        ]
      },
      {
        heading: '5.3 发货',
        path: '销售管理 → 发货管理',
        bullets: [
          '发货单号以 FH 开头。草稿可修改；已发货、已完成用「查看」看明细',
          '发货会生成已审核的发货出库单，单号以 FHCK 开头',
          '反发货会回冲库存并删除该发货对应的出库单（含未审核的残留草稿）',
          'App「发货出库」只列出未审核的 FHCK 草稿，不列出销售发货单'
        ]
      },
      {
        heading: '5.4 采购到货与外协',
        path: '供应管理 → 采购到货 / 外协单',
        text: '采购到货确认后增加库存，并生成采购入库单。外协按外协单收发。'
      }
    ]
  },
  {
    id: 'ch6',
    title: '六、设备维保',
    blocks: [
      {
        heading: '6.1 维保计划',
        path: '维护管理 → 维保计划',
        steps: [
          '维护周期、设备、负责人和下次到期。编码需要手填',
          '到期或强制「生成工单」后，得到一张状态为「计划」的维保工单，单号自动生成',
          '生成后，下次到期日按周期往后推'
        ]
      },
      {
        heading: '6.2 维保工单',
        path: '维护管理 → 维保工单',
        bullets: [
          '状态：计划、进行中、完成、取消。类型分保养和维修',
          '计划状态可以修改、开工、取消、删除。开工后设备变为维修中',
          '进行中可以完成或取消。完成要填处理结果；取消不填。两者都会把设备改回运行',
          'App 报修生成的是维修类型的维保工单，不是维保计划'
        ],
        tip: '源成本是维保分类标签，不计算金额，也不扣库存。源成本报表按这个标签统计工单。'
      }
    ]
  },
  {
    id: 'ch7',
    title: '七、车间 App 每日用法',
    blocks: [
      {
        heading: '7.1 工作台',
        bullets: [
          '「已开工工序」进入任务列表',
          '「当前计时」只展示，不能在这里开始或停止',
          '快捷入口：扫码报工、停机、报修'
        ]
      },
      {
        heading: '7.2 报工',
        steps: [
          '打开已开工任务，或扫描流转卡',
          '填写产出后先保存草稿',
          '确认页核对用料数量，选择仓库、投入批次和允许的替代料，再确认',
          '工序指导里的图纸、作业标准、点检和照片在单任务报工页查看'
        ]
      },
      {
        heading: '7.3 停机与报修',
        bullets: [
          '停机字段与 PC 停机登记一致：单号、产线、设备、工单、原因、起止时间和说明。未结束的停机可以补结束时间',
          '报修填写设备、负责人、源成本、计划时间和问题描述，保存后生成维修工单'
        ]
      }
    ]
  },
  {
    id: 'ch8',
    title: '八、分角色对照',
    blocks: [
      {
        heading: '计划 / 工艺（PC）',
        bullets: ['维护产品、工序、工艺', '开生产工单并受理开工', '开工工序任务', '看进度大屏和齐套']
      },
      {
        heading: '车间（App + PC）',
        bullets: ['绑定员工', '对已开工工序报工并确认', '登记停机、报修、报工异常', '需要时打卡']
      },
      {
        heading: '仓库（PC + App）',
        bullets: ['审核领料、入库、调拨', '在 App 执行当前仓库的草稿单据', '盘点、查库存', '核对发货出库']
      },
      {
        heading: '设备（PC）',
        bullets: ['维护设备和维保计划', '生成并开工维保工单', '完成后看源成本报表']
      }
    ]
  },
  {
    id: 'ch9',
    title: '九、常见问题',
    blocks: [
      {
        bullets: [
          'App 看不到工序：该工序还是待执行。先在 PC 工序任务里开工',
          'App 领料列表没有 PC 上的草稿：单头出库仓为空，或与 App 当前所选仓库不一致。报工确认自动扣的料是已审核单据，本来就不会出现在草稿列表',
          'App 发货列表和 PC 发货管理对不上：PC 是销售发货单（FH），App 只显示未审核的发货出库草稿（FHCK）',
          '报工数量改不动：确认时的扣料数量按工艺和合格数计算',
          '合格数报不上去：本道工序累计合格数不能超过本道计划数',
          '扫工单只看到部分工序：未开工的工序不会列出来',
          '维保工单进行中不能修改或再次开工：只有计划状态可以修改和开工；进行中用完成或取消',
          '下载 App：登录页扫码后用浏览器打开。安装包不会自动带上后来改的页面，改 App 后需要重新打包安装'
        ]
      }
    ]
  }
]

export default {
  name: 'HelpUserManual',
  data() {
    return {
      sections,
      activeId: 'ch1'
    }
  },
  mounted() {
    const q = this.$route.query.section
    if (q && sections.some(s => s.id === q)) {
      this.$nextTick(() => this.scrollTo(q))
    }
  },
  methods: {
    scrollTo(id) {
      this.activeId = id
      const el = document.getElementById(id)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }
}
</script>

<style scoped>
.manual {
  display: flex;
  gap: 20px;
  align-items: flex-start;
  max-width: 1100px;
}
.toc {
  position: sticky;
  top: 0;
  flex: 0 0 168px;
  max-height: calc(100vh - 160px);
  overflow: auto;
  padding: 12px 10px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}
.toc-title {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 8px;
  padding: 0 6px;
}
.toc a {
  display: block;
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 12px;
  color: #64748b;
  text-decoration: none;
  line-height: 1.4;
  margin-bottom: 2px;
}
.toc a:hover { background: #f1f5f9; color: #0f172a; }
.toc a.active {
  background: #eff6ff;
  color: #1d4ed8;
  font-weight: 600;
}
.manual-body { flex: 1; min-width: 0; }
.intro-box {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 18px 20px;
  margin-bottom: 14px;
}
.intro-box h2 {
  margin: 0 0 6px;
  font-size: 20px;
  color: #0f172a;
}
.intro-box > p {
  margin: 0 0 14px;
  color: #64748b;
  font-size: 14px;
}
.quick {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px 14px;
}
.quick-title {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 6px;
}
.quick ol {
  margin: 0;
  padding-left: 18px;
  color: #475569;
  font-size: 13px;
  line-height: 1.7;
}
.chapter {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px 20px 8px;
  margin-bottom: 12px;
  scroll-margin-top: 12px;
}
.chapter h3 {
  margin: 0 0 12px;
  font-size: 16px;
  color: #0f172a;
  padding-bottom: 10px;
  border-bottom: 1px solid #f1f5f9;
}
.block { margin-bottom: 14px; }
.block h4 {
  margin: 0 0 6px;
  font-size: 14px;
  color: #0f172a;
}
.path {
  margin: 0 0 6px;
  font-size: 12px;
  color: #2563eb;
  font-weight: 600;
}
.text {
  margin: 0 0 6px;
  font-size: 13px;
  color: #475569;
  line-height: 1.6;
}
.block ol, .block ul {
  margin: 0 0 6px;
  padding-left: 18px;
  color: #475569;
  font-size: 13px;
  line-height: 1.7;
}
.tip {
  margin: 6px 0 0;
  padding: 8px 10px;
  border-radius: 8px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #92400e;
  font-size: 12px;
  line-height: 1.5;
}
@media (max-width: 900px) {
  .manual { flex-direction: column; }
  .toc {
    position: static;
    flex: none;
    width: 100%;
    max-height: none;
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }
  .toc-title { width: 100%; }
  .toc a { display: inline-block; }
}
</style>
